import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Award, Users, TrendingUp } from 'lucide-react';

function AboutPage() {
  return (
    <>
      <Helmet>
        <title>Om CasinoGuide.dk - Din Guide til Udenlandske Casinoer</title>
        <meta 
          name="description" 
          content="Lær om CasinoGuide.dk og Jonas Schütt Nielsen. Vi hjælper danske spillere med at finde sikre udenlandske casinoer med hurtige udbetalinger og Trustly." 
        />
        <meta name="keywords" content="om casinoguide, jonas schütt nielsen, casino ekspert, udenlandske casinoer danmark" />
        <link rel="canonical" href="https://casinoguide.dk/om-os" />
      </Helmet>

      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-cyan-500/20 to-blue-600/20 blur-3xl -z-10"></div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                Om CasinoGuide.dk
              </span>
              <br />
              <span className="text-blue-100">Din Guide til Udenlandske Casinoer</span>
            </h1>
            <p className="text-lg text-blue-200 leading-relaxed">
              Velkomst til CasinoGuide.dk - Danmarks mest pålidelige kilde til information om udenlandske casinoer med hurtige udbetalinger, Trustly og sikre betalingsmetoder.
            </p>
          </div>

          {/* Mission */}
          <section className="mb-12">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-cyan-600/20">
              <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                Vores Mission
              </h2>
              <p className="text-blue-200 leading-relaxed mb-6">
                CasinoGuide.dk blev skabt for at hjælpe danske spillere med at navigere i det komplekse landskab af udenlandske casinoer. Vi tror på, at alle spillere fortjener adgang til sikre, hurtige og pålidelige casinoer - uanset om de har dansk licens eller ej.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-300 mb-3">Hvad vi gør:</h3>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Tester og anmelder udenlandske casinoer</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Sammenligner udbetalingstider og betalingsmetoder</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Forklarer lovgivning og skatteforhold</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Guider spillere til sikre og hurtige casinoer</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-green-300 mb-3">Hvad vi står for:</h3>
                  <ul className="space-y-2 text-green-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2 mt-1">•</span>
                      <span>100% ærlige og upartiske anmeldelser</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2 mt-1">•</span>
                      <span>Gennemsigtige interessekonflikter</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2 mt-1">•</span>
                      <span>Fokus på spillersikkerhed og ansvarligt spil</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2 mt-1">•</span>
                      <span>Opdaterede informationer og guides</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* About Jonas */}
          <section className="mb-12">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-blue-600/20">
              <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                Om Jonas Schütt Nielsen
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <p className="text-blue-200 leading-relaxed mb-6">
                    Jeg er Jonas Schütt Nielsen, grundlæggeren af CasinoGuide.dk. Som dansk full-stack udvikler bosat i Skopje, Makedonien, har jeg en unik indsigt i både det danske og det internationale casinomarked.
                  </p>
                  <p className="text-blue-200 leading-relaxed mb-6">
                    Min baggrund som softwareudvikler giver mig de tekniske færdigheder til at evaluere casinoers sikkerhed, betalingssystemer og brugeroplevelse på et dybt niveau. Jeg forstår, hvordan Trustly, kryptovalutaer og e-wallets fungerer - ikke kun fra en brugers perspektiv, men også fra den tekniske side.
                  </p>
                  <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-cyan-300 mb-3">Min Erfaring</h3>
                    <div className="space-y-2 text-cyan-200 text-sm">
                      <p>• <strong>Full Stack Development</strong> - QinShift Academy/Seavus Academy (2023-2024)</p>
                      <p>• <strong>Udvikler hos Innohub</strong> - Arbejdede på Pazarluk.mk e-commerce platform</p>
                      <p>• <strong>Freelance udvikler</strong> - Specialiserer i danske projekter via Upwork</p>
                      <p>• <strong>Teknisk ekspertise:</strong> React, C#, Python, PostgreSQL, Docker</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-600/20">
                    <h3 className="text-lg font-bold text-blue-300 mb-3">Kontakt Information</h3>
                    <div className="space-y-2 text-blue-200 text-sm">
                      <p><strong>Email:</strong> jonassnk11@gmail.com</p>
                      <p><strong>Telefon:</strong> +389 75 433 740</p>
                      <p><strong>Lokation:</strong> Skopje, Nord Makedonien</p>
                      <p><strong>GitHub:</strong> github.com/jsnk11</p>
                    </div>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-600/20">
                    <h3 className="text-lg font-bold text-green-300 mb-3">Kvalifikationer</h3>
                    <div className="space-y-2 text-green-200 text-sm">
                      <p>• Native dansk sproglærer</p>
                      <p>• Teknisk casino-analyse</p>
                      <p>• International gaming erfaring</p>
                      <p>• Betalingssystem ekspertise</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Values */}
          <section className="mb-12">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/30">
              <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
                Vores Værdier
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-300 mb-2">Sikkerhed Først</h3>
                      <p className="text-blue-200 text-sm">
                        Vi anbefaler kun casinoer med gyldige licenser og stærk sikkerhed. Spillernes beskyttelse er altid vores første prioritet.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-300 mb-2">Gennemsigtighed</h3>
                      <p className="text-green-200 text-sm">
                        Vi er åbne om vores affiliations og hvordan vi tjener penge. Ærlige anmeldelser er fundamentet for vores troværdighed.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-purple-300 mb-2">Spillerfokus</h3>
                      <p className="text-purple-200 text-sm">
                        Alt hvad vi gør, gøres med spillerens bedste interesser for øje. Vi prioriterer hurtige udbetalinger og fair vilkår.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-yellow-300 mb-2">Konstant Forbedring</h3>
                      <p className="text-yellow-200 text-sm">
                        Vi opdaterer løbende vores guides og anmeldelser for at sikre, at informationen altid er aktuel og relevant.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="mb-12">
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6">
              <h2 className="text-xl font-bold text-red-300 mb-4">Vigtigt at Vide</h2>
              <div className="space-y-3 text-red-200 text-sm">
                <p>
                  <strong>Affiliate Disclosure:</strong> CasinoGuide.dk modtager kommission fra nogle casinoer vi linker til. Dette påvirker ikke vores anmeldelser eller anbefalinger - vi anbefaler kun casinoer vi selv ville bruge.
                </p>
                <p>
                  <strong>Ansvarligt Spil:</strong> Vi opfordrer altid til ansvarligt spil og anbefaler kun at spille med penge du har råd til at tabe. Hvis du har problemer med spil, kontakt ROFUS.nu eller StopSpillet.dk.
                </p>
                <p>
                  <strong>Juridisk Disclaimer:</strong> Informationen på CasinoGuide.dk er til uddannelses- og underholdningsformål. Spillovgivning varierer mellem jurisdiktioner, og spillere er selv ansvarlige for at overholde lokale love.
                </p>
              </div>
            </div>
          </section>

          {/* Contact CTA */}
          <section>
            <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-2xl p-8 border border-cyan-500/30 text-center">
              <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                Har du Spørgsmål?
              </h2>
              <p className="text-blue-200 mb-6">
                Jeg hjælper gerne med at besvare dine spørgsmål om udenlandske casinoer, hurtige udbetalinger eller Trustly.
              </p>
              <a 
                href="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all duration-200"
              >
                Kontakt Mig
              </a>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default AboutPage;